import { useState } from 'react';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { Save, AlertCircle } from 'lucide-react';
import { validateUsername } from '../../utils/validation';

interface AccountSettingsProps {
  username: string;
  onUpdate: (username: string) => void;
}

export function AccountSettings({ username, onUpdate }: AccountSettingsProps) {
  const [newUsername, setNewUsername] = useState(username);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    setError('');

    try {
      // Validate username format
      if (!validateUsername(newUsername)) {
        throw new Error('Username can only contain letters, numbers, and hyphens');
      }

      // Check if username is already taken
      const q = query(collection(db, 'users'), where('username', '==', newUsername));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty && querySnapshot.docs[0].id !== user.uid) {
        throw new Error('Username is already taken');
      }

      // Update username in user document
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { username: newUsername });
      onUpdate(newUsername);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update username');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-muted mb-2">Username</label>
        <div className="flex items-center space-x-4">
          <input
            type="text"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value.toLowerCase())}
            className="flex-1 rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-default input-focus p-2"
            placeholder="Enter username"
          />
          <button
            onClick={handleSave}
            disabled={saving || newUsername === username}
            className="btn-primary whitespace-nowrap"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Update Username'}
          </button>
        </div>
        <p className="mt-2 text-sm text-muted">
          Your calculator URL: {window.location.origin}/{newUsername}
        </p>
      </div>
    </div>
  );
}